import { SafePromise, safeTimeout } from '@benzinga/safe-await';
import { ContentManager, Term } from '@benzinga/content-manager';
import { Session } from '@benzinga/session';
import { isExpiredUrlActive } from '../expiredUrlSlug';
import { ArticleManager } from '../manager';
import type { GetArticleI, InternalTag } from '../entities/article';

const TAXONOMY_LOOKUP_TIMEOUT_MS = 2500;
const EXPIRED_URL_CHECK_TIMEOUT_MS = 1000;

const fallbackTermsFromArticle = (article: GetArticleI): Term[] => {
  const channels = article.Channels ?? [];
  const tags = article.Tags ?? [];
  return [...channels, ...tags].map((tag: InternalTag) => ({
    name: tag.name,
    parent_id: '0',
    tid: String(tag.tid),
    type: String(tag.vid) === '1' ? 'Channels' : 'Tags',
    url_drupal: '',
    url_public: '',
    vid: String(tag.vid),
  }));
};

export const getArticleTerms = async (session: Session, article: GetArticleI): SafePromise<Term[]> => {
  const channels = article.Channels ? article.Channels : [];
  const tags = article.Tags ? article.Tags : [];
  const tids = [...new Set(channels.map(channel => channel.tid).concat(tags.map(tag => tag.tid)))];
  if (tids.length === 0) return { ok: [] };

  const contentManager = session.getManager(ContentManager);
  const articleManager = session.getManager(ArticleManager);
  const termRes = await safeTimeout(contentManager.getTermById(tids.join(',')), TAXONOMY_LOOKUP_TIMEOUT_MS);
  const terms = Array.isArray(termRes.ok) && termRes.ok.length > 0 ? termRes.ok : fallbackTermsFromArticle(article);

  const results = await Promise.allSettled(
    terms.map(async (term: Term) => {
      if (!term.url_public) {
        return term;
      }

      try {
        const expiredUrlCheck = await safeTimeout(
          articleManager.checkExpiredUrl('topic', term.url_public),
          EXPIRED_URL_CHECK_TIMEOUT_MS,
        );
        if (expiredUrlCheck.err) {
          return term;
        }
        const isValid = !isExpiredUrlActive(expiredUrlCheck.ok);
        return isValid ? term : null;
      } catch (error) {
        console.error('Error checking expired URL for term:', term.url_public, error);
        return term;
      }
    }),
  );

  const validTerms = results
    .filter((result): result is PromiseFulfilledResult<Term> => result.status === 'fulfilled' && result.value !== null)
    .map(result => result.value);

  return {
    ok: validTerms,
  };
};
